import { clearAccountWorkspacesCache } from "@/api/account/workspaces";
import { clearAllUserProfileCache, loginApi, signinup, updateUser } from "@/api/auth/actions";
import { clearRealtimeTokenCache } from "@/api/realtimeToken";
import { alertShow } from "@/components/Alert/AlertHook";
import {
  LOGIN_USER_EMAIL,
  OAUTHCODE,
  OAUTHSTATE,
  SIGNINUPTYPE,
  SPJWT,
  SPTENANTID,
  SPUSERGUID,
  TOKEN_EXPIRY
} from "@/configs";
import { isProtectedRoute } from "@/configs/protectedRoutes";
import { PATH } from "@/configs/router/routerConfig";
import { persistSuperTokensSessionFromResponse } from "@/lib/auth/persistSuperTokensSession";
import { hasPendingWorkspaceSelection } from "@/lib/auth/sessionUser";
import { ensureSuperTokensInitialized } from "@/lib/auth/supertokensInit";
import { logger, setLoggerContext } from "@/lib/logger";
import { UserPreferences } from "@/models/user";
import { hydrateAvatarFields, normalizeAvatarFields } from "@/slices/auth/authAvatar";
import { login as loginAction, logout, setLoading, update as updateAction } from "@/slices/auth/authReducer";
import {
  type LogoutRedirectTarget,
  clearPendingLogoutRedirectTarget,
  setPendingLogoutRedirectTarget
} from "@/slices/auth/logoutRedirect";
import { persistor, store } from "@/store";
import { isPublicAuthPath } from "@/utils/authRoutes";
import { decode } from "@/utils/jwt";
import { removeCookie, setCookie } from "@/utils/session";
import { createAsyncThunk } from "@reduxjs/toolkit";

import { clearMessage } from "../firebase/firebaseReducer";
import { resolvePersistedTenantId } from "./tenantPersistence";

function hasRequiredSessionHeaders(headers: Record<string, string | undefined> | undefined) {
  return Boolean(headers?.["st-access-token"] && headers?.["st-refresh-token"] && headers?.["front-token"]);
}

function seedTokenExpiryCookie(decoded: unknown) {
  const rawExpiry = Number((decoded as { exp?: unknown } | null | undefined)?.exp);
  if (!Number.isFinite(rawExpiry) || rawExpiry <= 0) {
    return;
  }

  const expiryMs = rawExpiry > 1_000_000_000_000 ? rawExpiry : rawExpiry * 1000;
  setCookie(TOKEN_EXPIRY, String(expiryMs));
}

function resolveLogoutRedirectTarget(redirectTo?: LogoutRedirectTarget) {
  if (redirectTo) {
    return redirectTo;
  }

  if (typeof window === "undefined") {
    return PATH.LOGIN;
  }

  return {
    pathname: PATH.LOGIN,
    query: {
      backTo: `${window.location.pathname}${window.location.search}`
    }
  };
}

export const loginUser = createAsyncThunk(
  "login/loginUser",
  async (
    body: {
      email: string;
      password: string;
      tenantId: string;
      recaptchaToken?: string | null;
    },
    thunkAPI
  ) => {
    try {
      ensureSuperTokensInitialized();
      thunkAPI.dispatch(setLoading(true));
      const header = {
        "sp-tenant-id": body.tenantId, // todo add to form, signup form name is tenant for now
        "st-auth-mode": "header" // must change supertoken recipe to make this work
      };
      const login: any = await loginApi(
        {
          email: body.email,
          password: body.password,
          ...(body.recaptchaToken ? { "recaptcha-token": body.recaptchaToken } : {})
        },
        header
      );
      const status = login.content.status;
      if (status === "OK") {
        if (hasRequiredSessionHeaders(login.headers)) {
          setCookie(SPTENANTID, body.tenantId);
        }
        await persistSuperTokensSessionFromResponse({
          headers: login.headers,
          status: login.status,
          source: "loginUser"
        });
        const stAccessToken = login.headers["st-access-token"];
        const user = login.content.user;
        const decoded = stAccessToken ? decode(stAccessToken) : {};
        seedTokenExpiryCookie(decoded);
        if (user.email && user.email.endsWith("firstTimeLogin")) {
          user.firstLogin = true;
        }
        user.spUserGuid = decoded.spUGuid || user.spUserGuid;
        user.stUserGuid = decoded.sub || user.stUserGuid;
        user.id = decoded.spUGuid || user.id;
        user.email = decoded.spEmail || user.email;
        user.role = decoded.role ?? user.role; // 0: admin, 1: user user.role
        user.groups = decoded.groups || user.groups;
        user.tenantName = decoded.spTid || user.tenantName;
        user.tenantGuid = decoded.spTGuid || user.tenantGuid;
        user.tenantKind = decoded.spTKind || user.tenantKind;
        user.spAId = decoded.spAId || user.spAId;
        user.accountGuid = decoded.spAId || user.accountGuid;
        user.pendingWorkspaceSelection = hasPendingWorkspaceSelection({
          pendingWorkspaceSelection: decoded.spPendingWorkspaceSelection,
          spPendingWorkspaceSelection: decoded.spPendingWorkspaceSelection,
          tenantKind: decoded.spTKind
        });
        user.onboardingStatus = decoded.spTOnboardingStatus || user.onboardingStatus;
        user.fullName = decoded.fullName || user.fullName;
        user.firstName = decoded.firstName || user.firstName || "";
        user.lastName = decoded.lastName || user.lastName || "";
        user.phoneNumber = decoded.phoneNumber || user.phoneNumber;
        // Prefer explicit values from login.content.user, otherwise fallback to JWT custom claims if present.
        // Keep existing user property if neither is provided.
        // transcription_model removed: model fixed by triage_agent config
        // user.transcription_model =
        //   (login.content.user && (login.content.user.transcription_model ?? undefined)) ??
        //   decoded.transcription_model ??
        //   user.transcription_model;
        user.transcription_language =
          (login.content.user && (login.content.user.transcription_language ?? undefined)) ??
          decoded.transcription_language ??
          user.transcription_language;

        user.twilio_verified_caller_id =
          (login.content.user && (login.content.user.twilio_verified_caller_id ?? undefined)) ??
          decoded.twilio_verified_caller_id ??
          user.twilio_verified_caller_id;
        user.isOauthUser = decoded.is_oauth_user || false;
        user.isSandboxTenant = Boolean(decoded?.spPTGuid);
        user.spPTGuid = decoded?.spPTGuid;
        const authenticatedUser = normalizeAvatarFields(user);

        setCookie(SPJWT, stAccessToken);
        setCookie(SPTENANTID, resolvePersistedTenantId(authenticatedUser, body.tenantId));
        setCookie(SPUSERGUID, user.spUserGuid);
        setCookie(LOGIN_USER_EMAIL, body.email);
        store.dispatch(clearMessage());

        setLoggerContext({ userId: user.spUserGuid, tenantId: user.tenantGuid });

        thunkAPI.dispatch(
          loginAction({
            user: authenticatedUser,
            role: authenticatedUser.role
          })
        );
        if (typeof window !== "undefined" && !window.location.pathname.startsWith("/auth/callback")) {
          void hydrateAvatarFields(authenticatedUser).then((hydratedUser) => {
            if (hydratedUser.avatar || hydratedUser.profile_pic) {
              thunkAPI.dispatch(updateAction({ user: hydratedUser }));
            }
          });
        }
        thunkAPI.dispatch(setLoading(false));
        const twoFaClaim = decoded?.["2fa-completed"];
        const isTwoFaComplete = typeof twoFaClaim === "object" ? Boolean((twoFaClaim as any).v) : Boolean(twoFaClaim);
        if (!isTwoFaComplete) {
          return {
            user: authenticatedUser,
            role: authenticatedUser.role,
            requires2FA: true,
            sessionId: login.content.sessionId,
            message: "2FA_REQUIRED"
          };
        }
      } else {
        logger.error("login failed", { status });
        thunkAPI.dispatch(setLoading(false));
        return thunkAPI.rejectWithValue({
          message: login.content?.message || status,
          code: login.content?.code,
          detail: login.content?.detail
        });
      }
    } catch (error: any) {
      thunkAPI.dispatch(setLoading(false));
      sessionStorage.removeItem(OAUTHCODE);
      sessionStorage.removeItem(OAUTHSTATE);
      sessionStorage.removeItem(SIGNINUPTYPE);
      alertShow({ message: error.localizeMessage || error.message || error.toString(), severity: "error" });
      return thunkAPI.rejectWithValue({
        message: error.message || error.toString(),
        code: error.code || error.content?.code,
        detail: error.detail || error.content?.detail
      });
    }
  }
);

export const logOutUser = createAsyncThunk(
  "logout/logoutUser",
  async (options: { redirectTo?: LogoutRedirectTarget } = {}, thunkAPI) => {
    logger.info("logOutUser thunk started");
    const safe = (fn: () => void, label: string) => {
      try {
        fn();
      } catch (err) {
        logger.error(`logout cleanup error: ${label}`, { err });
      }
    };

    const redirectTarget = resolveLogoutRedirectTarget(options.redirectTo);
    setPendingLogoutRedirectTarget(redirectTarget);

    try {
      ensureSuperTokensInitialized();
      const Session = require("supertokens-auth-react/recipe/session").default;
      if (Session) {
        await Session.signOut();
        logger.info("SuperTokens signOut completed");
      }
    } catch (err) {
      logger.error("SuperTokens signOut error", { err });
    }

    safe(() => removeCookie(SPJWT), "SPJWT");
    safe(() => removeCookie(SPTENANTID), "SPTENANTID");
    safe(() => removeCookie(SPUSERGUID), "SPUSERGUID");
    safe(() => removeCookie(LOGIN_USER_EMAIL), "LOGIN_USER_EMAIL");
    safe(() => removeCookie(TOKEN_EXPIRY), "TOKEN_EXPIRY");
    clearAllUserProfileCache();
    clearAccountWorkspacesCache();
    clearRealtimeTokenCache();

    safe(() => sessionStorage.removeItem(OAUTHCODE), "OAUTHCODE");
    safe(() => sessionStorage.removeItem(OAUTHSTATE), "OAUTHSTATE");
    safe(() => sessionStorage.removeItem(SIGNINUPTYPE), "SIGNINUPTYPE");
    safe(() => sessionStorage.clear(), "sessionStorage.clear");
    safe(() => localStorage.clear(), "localStorage.clear");

    thunkAPI.dispatch(logout());

    try {
      persistor.pause();
      await persistor.flush();
      await persistor.purge();
      persistor.persist();
      logger.info("Redux persist store purged on logout");
    } catch (err) {
      logger.error("Redux persist purge error during logout", { err });
    }

    if (typeof window !== "undefined") {
      const router = require("next/router").default;
      if (router) {
        const pathname = window.location.pathname;
        const shouldRedirectImmediately = isPublicAuthPath(pathname) || !isProtectedRoute(pathname);
        if (shouldRedirectImmediately) {
          logger.info("logOutUser: Redirecting immediately on non-RouteGuard route");
          clearPendingLogoutRedirectTarget();
          router.replace(redirectTarget);
        } else {
          logger.info("logOutUser: Deferring redirect to RouteGuard on protected route");
        }
      }
    }

    return true;
  }
);

export const loginUserWithOauth = createAsyncThunk(
  "login/loginUser",
  async (
    body: {
      thirdPartyId: string;
      // clientType: string;
      "invitation-code"?: string;
      "sp-tenant-id": string;
      "signinup-type"?: string;
      "signup-intent-confirmed"?: string;
      shouldTryLinkingWithSessionUser?: boolean;
      redirectURIInfo: {
        redirectURIOnProviderDashboard: string;
        redirectURIQueryParams: {
          code: string;
          state: string;
        };
        // pkceCodeVerifier: "abcd1234";
      };
    },
    thunkAPI
  ) => {
    try {
      ensureSuperTokensInitialized();
      thunkAPI.dispatch(setLoading(true));
      const header = {
        "sp-tenant-id": body["sp-tenant-id"]
      };
      const login: any = await signinup(
        body,
        header,
        body.shouldTryLinkingWithSessionUser ? { withToken: true } : undefined
      );
      const status = login.content.status;

      if (status === "OK") {
        if (hasRequiredSessionHeaders(login.headers)) {
          setCookie(SPTENANTID, body["sp-tenant-id"]);
        }
        await persistSuperTokensSessionFromResponse({
          headers: login.headers,
          status: login.status,
          source: "loginUserWithOauth",
          fireSessionUpdateEvent:
            typeof window === "undefined" ? true : !window.location.pathname.startsWith("/auth/callback")
        });
        const stAccessToken = login.headers["st-access-token"];
        const user = login.content.user;
        const decoded = stAccessToken ? decode(stAccessToken) : {};
        seedTokenExpiryCookie(decoded);

        if (user.email && user.email.endsWith("firstTimeLogin")) {
          user.firstLogin = true;
        }
        user.spUserGuid = decoded.spUGuid || user.spUserGuid;
        user.email = decoded.spEmail || user.email;
        user.stUserGuid = decoded.sub || user.stUserGuid;
        user.id = decoded.spUGuid || user.id;
        user.role = decoded.role ?? user.role; // 0: admin, 1: user user.role
        user.groups = decoded.groups || user.groups;
        user.tenantName = decoded.spTid || user.tenantName;
        user.tenantGuid = decoded.spTGuid || user.tenantGuid;
        user.tenantKind = decoded.spTKind || user.tenantKind;
        user.spAId = decoded.spAId || user.spAId;
        user.accountGuid = decoded.spAId || user.accountGuid;
        user.pendingWorkspaceSelection = hasPendingWorkspaceSelection({
          pendingWorkspaceSelection: decoded.spPendingWorkspaceSelection,
          spPendingWorkspaceSelection: decoded.spPendingWorkspaceSelection,
          tenantKind: decoded.spTKind
        });
        // Ensure user's name is set from JWT or backend response
        user.fullName = decoded.fullName || user.fullName || user.name || "";
        user.firstName = decoded.firstName || user.firstName || "";
        user.lastName = decoded.lastName || user.lastName || "";
        user.phoneNumber = decoded.phoneNumber || user.phoneNumber;
        user.isSandboxTenant = Boolean(decoded.spPTGuid);
        user.spPTGuid = decoded.spPTGuid;
        user.isOauthUser = true;

        user.twilio_verified_caller_id =
          (login.content.user && (login.content.user.twilio_verified_caller_id ?? undefined)) ??
          decoded.twilio_verified_caller_id ??
          user.twilio_verified_caller_id;

        user.transcription_language =
          (login.content.user && (login.content.user.transcription_language ?? undefined)) ??
          decoded.transcription_language ??
          user.transcription_language;

        const authenticatedUser = normalizeAvatarFields(user);

        setCookie(SPJWT, stAccessToken);
        setCookie(SPTENANTID, resolvePersistedTenantId(authenticatedUser, body["sp-tenant-id"]));
        setCookie(SPUSERGUID, user.spUserGuid);
        setCookie(LOGIN_USER_EMAIL, user.email);
        store.dispatch(clearMessage());

        setLoggerContext({ userId: user.spUserGuid, tenantId: user.tenantGuid });

        thunkAPI.dispatch(
          loginAction({
            user: authenticatedUser,
            role: authenticatedUser.role
          })
        );
        if (typeof window !== "undefined" && !window.location.pathname.startsWith("/auth/callback")) {
          void hydrateAvatarFields(authenticatedUser).then((hydratedUser) => {
            if (hydratedUser.avatar || hydratedUser.profile_pic) {
              thunkAPI.dispatch(updateAction({ user: hydratedUser }));
            }
          });
        }
        thunkAPI.dispatch(setLoading(false));

        // 2FA enforcement: if claim is not complete, route user to 2FA flow.
        // Keep parity with the email/password login flow.
        try {
          const twoFaClaim = decoded?.["2fa-completed"];
          const isTwoFaComplete = typeof twoFaClaim === "object" ? Boolean((twoFaClaim as any).v) : Boolean(twoFaClaim);
          if (!isTwoFaComplete) {
            return {
              user: authenticatedUser,
              role: authenticatedUser.role,
              requires2FA: true,
              sessionId: login.content.sessionId,
              message: "2FA_REQUIRED"
            };
          }
        } catch (e) {
          logger.error("loginUserWithOauth: failed reading 2fa-completed claim", { e });
        }

        return {
          user: authenticatedUser,
          role: authenticatedUser.role,
          requires2FA: false
        };
      } else if (status === "SIGN_IN_UP_NOT_ALLOWED") {
        // the reason string is a user friendly message
        // about what went wrong. It can also contain a support code which users
        // can tell you so you know why their sign in / up was not allowed.
        // window.alert(response.reason);
        // Fix: login.content.reason is an object; its `reason` field is a JSON string.
        let parsedReason: any = {};
        try {
          const r = login.content?.reason;
          // Case 1: reason.reason contains JSON string
          if (r && typeof r.reason === "string") {
            parsedReason = JSON.parse(r.reason);
          }
          // Case: already parsed object
          else if (r && typeof r.reason === "object") {
            parsedReason = r.reason;
          }
          // Case 2: entire reason is a JSON string
          else if (typeof r === "string") {
            parsedReason = JSON.parse(r);
          }
        } catch (e) {
          logger.error("Failed parsing SIGN_IN_UP_NOT_ALLOWED reason", { raw: login.content?.reason });
        }

        const code = parsedReason?.code || parsedReason?.status;
        if (code === "OAUTH_DISCOVERY" || code === "MULTI_TENANT") {
          logger.info("SIGN_IN_UP_NOT_ALLOWED OAUTH_DISCOVERY or MULTI_TENANT", { reason: login.content });
        } else {
          logger.error("SIGN_IN_UP_NOT_ALLOWED 2", { reason: login.content });
        }
        thunkAPI.dispatch(setLoading(false));
        return thunkAPI.rejectWithValue({ status: login.content.status, message: login.content.reason });
      } else if (status === "GENERAL_ERROR" && login.content.message) {
        logger.error("GENERAL_ERROR  ", { reason: login.content });
        thunkAPI.dispatch(setLoading(false));
        return thunkAPI.rejectWithValue({ status: login.content.status, message: login.content.message });
      } else {
        logger.error("login failed", { status });
        thunkAPI.dispatch(setLoading(false));
        return thunkAPI.rejectWithValue({ status: status, message: status });
      }
    } catch (error: any) {
      thunkAPI.dispatch(setLoading(false));
      alertShow({ message: error.localizeMessage || error.message || error.toString(), severity: "error" });
      return thunkAPI.rejectWithValue({ status: error.status, message: error.message || error.toString() });
    }
  }
);
export const updateUserPreferences = createAsyncThunk(
  "auth/updateUserPreferences",
  async (preferences: UserPreferences, thunkAPI) => {
    const state: any = thunkAPI.getState();
    const user = state.auth?.user;

    if (!user?.id) {
      return thunkAPI.rejectWithValue("User not logged in");
    }

    const basePrefs = user.preferences || {};
    const updatedPreferences = {
      ...basePrefs,
      ...preferences
    };

    // Merge completed_tours if it's not empty; if empty, it's an intentional reset
    if (preferences.completed_tours) {
      if (preferences.completed_tours.length === 0) {
        updatedPreferences.completed_tours = [];
      } else {
        updatedPreferences.completed_tours = Array.from(
          new Set([...(basePrefs.completed_tours || []), ...preferences.completed_tours])
        );
      }
    }

    // Prevent UI from accidentally writing a non-object tenant_config.
    if (Object.prototype.hasOwnProperty.call(preferences, "tenant_config")) {
      const tc = (preferences as any).tenant_config;
      if (tc !== null && tc !== undefined && typeof tc !== "object") {
        return thunkAPI.rejectWithValue("tenant_config must be an object or null");
      }
    }

    try {
      // 1. Update backend
      await updateUser({ preferences: updatedPreferences }, user.id);

      // 2. Update local state
      thunkAPI.dispatch(
        updateAction({
          user: {
            ...user,
            preferences: updatedPreferences
          }
        })
      );

      return updatedPreferences;
    } catch (error: any) {
      logger.error("updateUserPreferences failed", { error });
      return thunkAPI.rejectWithValue(error.message || "Failed to update preferences");
    }
  }
);
